import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import Gap from 'src/components/Gap';
import Image from 'src/components/Image';
import PeerTimeIconBase from 'src/components/PeerTimeIcon';
import ChangeNicknameButton from 'src/components/ProfileModal/Full/ChangeNicknameButton';
import { useReportAndBlock } from 'src/components/ProfileModal/Full/useReportAndBlock';
import QualityUserBadgeBase from 'src/components/QualityUserBadge';
import UserCodeBase from 'src/components/UserCode';
import { GENDER } from 'src/types/register';
import { hexToRgb } from 'src/utils/common';
import { getLocaleTimeString } from 'src/utils/date';
import QualityUserCard from './QualityUserCard';
import SubscriptionCard from './SubscriptionCard';
import { useProfileCardContext } from '..';
const UserCode = styled(UserCodeBase)`
  margin-bottom: 16px;
`;
const PreferredLanguage = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const PreferredLanguageIcon = styled(dynamic(() => import('src/assets/images/icPreferLanguageFill.svg')))`
  width: 16px;
  height: 16px;
  color: ${({
  theme
}) => theme.color.content8};
`;
const Wrap = styled.div`
  position: relative;
  padding: 0 20px 24px;
`;
const ReportText = styled.button`
  color: ${({
  theme
}) => theme.color.content8};
  text-align: center;
  width: 100%;

  font-size: 14px;
  font-weight: 700;
  background-color: transparent;
  border: none;
`;
const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({
  theme
}) => theme.color.surface4};
`;
const ProfileInfoName = styled.div`
  color: ${({
  theme
}) => theme.color.content12};
  font-size: 26px;
  font-weight: 700;
  line-height: 36px;
  word-break: break-all;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const Section = styled.div`
  padding: 24px 0px;
`;
const ProfileInfoInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const QualityUserBadge = styled(QualityUserBadgeBase)`
  flex-shrink: 0;

  width: 20px;
  height: 20px;
`;
const Country = styled.div`
  display: inline-block;
  vertical-align: middle;
`;
const CountryIcon = styled(Image)`
  width: 16px;
  vertical-align: middle;
  display: inline-block;
`;
const CountryCode = styled.span`
  display: inline-block;
  color: ${({
  theme
}) => theme.color.content12};
  display: inline-block;
  vertical-align: middle;

  /* Regular/Regular 12 */
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;
const InlineGap = styled(Gap)`
  display: inline-block;
`;
const GenderWrap = styled.div`
  display: inline-block;
  vertical-align: middle;
`;
const GenderIcon = styled.img`
  width: 16px;
  height: 16px;
  display: inline-block;
  vertical-align: middle;
`;
const PeerTimeIcon = styled(PeerTimeIconBase)`
  width: 16px;
  height: 16px;
  display: inline-block;
  vertical-align: middle;
  color: ${({
  theme
}) => theme.color.content12};
`;
const GenderText = styled.div`
  display: inline-block;
  vertical-align: middle;
  color: ${({
  theme
}) => theme.color.content12};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: -1px;
`;
const Bio = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12};
  overflow-wrap: break-word;
  white-space: pre-line;
`;
const HashtagWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const Hashtag = styled.div`
  border-radius: 360px;
  background: rgba(${({
  theme
}) => hexToRgb(theme.color.accentD1)}, 0.1);
  color: ${({
  theme
}) => theme.color.accentD1};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  display: inline-flex;
  height: 34px;
  padding: 8px 12px 8px 16px;
  align-items: center;
  gap: 4px;
`;
const SectionTitle = styled.div`
  color: ${({
  theme
}) => theme.color.content8};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 16px;
`;
interface Props {
  anchor?: 'hashtags';
}
const ProfileInfo: React.FC<Props> = ({
  anchor
}) => {
  const {
    profile
  } = useProfileCardContext();
  const {
    canReport,
    handleClickReportOrBlock
  } = useReportAndBlock({
    target: 'text'
  });
  const isMine = profile.type === 'MY_PROFILE';
  const {
    t
  } = useTranslation();
  const genderText = useMemo(() => {
    if (!profile || !profile.gender) return '';
    if (profile.gender === GENDER.MALE) return t('gender_male');
    if (profile.gender === GENDER.FEMALE) return t('gender_female');
    return t(profile.gender);
  }, [profile, t]);
  const anchorCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      requestAnimationFrame(() => {
        node.scrollIntoView();
      });
    }
  }, []);
  return <Wrap data-sentry-element="Wrap" data-sentry-component="ProfileInfo" data-sentry-source-file="index.tsx">
      <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
        <Row data-sentry-element="Row" data-sentry-source-file="index.tsx">
          <ProfileInfoName data-sentry-element="ProfileInfoName" data-sentry-source-file="index.tsx">{profile.nickname}</ProfileInfoName>
          {!!profile?.qualityUserBadge && <>
              <Gap width={8} />
              <QualityUserBadge qualityUserBadge={profile.qualityUserBadge} showModalOnClick={true} showFallbackImage={true} isMine={isMine} />
            </>}
          {isMine && <>
              <Gap width={8} />
              <ChangeNicknameButton />
            </>}
        </Row>
        <UserCode profile={profile} data-sentry-element="UserCode" data-sentry-source-file="index.tsx" />
        <ProfileInfoInner data-sentry-element="ProfileInfoInner" data-sentry-source-file="index.tsx">
          {profile.location && <Country>
              <CountryIcon src={`/images/flags/icFlag${profile.location.countryCode}.png`} />
              <InlineGap width={8} />
              <CountryCode>{profile.location.country}</CountryCode>
            </Country>}
          {profile.location?.timeZoneOffset && <GenderWrap>
              <PeerTimeIcon timeZoneId={profile.location.timeZoneId} />
              <InlineGap width={8} />
              <GenderText>
                {getLocaleTimeString(new Date(new Date().getTime() + (new Date().getTimezoneOffset() * 60 + profile.location.timeZoneOffset) * 1000))}
              </GenderText>
            </GenderWrap>}
          {profile.gender && <GenderWrap>
              <GenderIcon src={`/images/icons/icGender${profile.gender}.svg`} alt='gender' />
              <InlineGap width={4} />
              <GenderText>{genderText}</GenderText>
            </GenderWrap>}
        </ProfileInfoInner>
      </Section>
      {!!profile.userPreferredLanguageEntries?.length && <>
          <SectionDivider />
          <Section>
            <PreferredLanguage>
              <PreferredLanguageIcon />
              <Bio>
                {profile.userPreferredLanguageEntries.map(({
              displayName
            }) => displayName).join(', ')}
              </Bio>
            </PreferredLanguage>
          </Section>
        </>}
      {isMine && <>
          <SectionDivider />
          <Section>
            <SectionTitle>{t('vividi_profile_section_title')}</SectionTitle>
            <SubscriptionCard />
          </Section>
          {profile.qualityUserBadge && <>
              <SectionDivider />
              <Section>
                <SectionTitle>{t('badge_progress_title')}</SectionTitle>
                <QualityUserCard qualityUserBadge={profile.qualityUserBadge} />
              </Section>
            </>}
        </>}
      {profile.profileBio && <>
          <SectionDivider />
          <Section>
            <SectionTitle>{t('PROFILE_ABOUT_ME')}</SectionTitle>
            <Bio>{profile.profileBio}</Bio>
          </Section>
        </>}
      {!!profile.hashtagInfos?.length && <>
          <SectionDivider />
          <Section ref={anchor === 'hashtags' ? anchorCallbackRef : null}>
            <SectionTitle>{t('PROFILE_HASHTAG')}</SectionTitle>
            <HashtagWrap>
              {profile.hashtagInfos.map(({
            hashtag
          }) => <Hashtag key={`ProfileModal-hashtag-${hashtag}`}>#{hashtag}</Hashtag>)}
            </HashtagWrap>
          </Section>
        </>}
      {/* 매치중에는 전용 신고 버튼이 노출되기 때문에 프로필 모달에서 따로 노출하지 않음 */}
      {canReport && <>
          <SectionDivider />
          <Section>
            <ReportText onClick={handleClickReportOrBlock}>
              {t('PROFILE_REPORTANDBLOCK')}
            </ReportText>
          </Section>
        </>}
    </Wrap>;
};
export default ProfileInfo;